<div class="feature-list-container">
  <!-- Page Title -->
  <div class="page-title-section">
    <h1 class="page-title">Feature Management</h1>
    <p class="page-subtitle">Manage feature flags and control feature rollouts</p>
  </div>

  <!-- Unified Top Bar with Filters and Actions -->
  <div class="header-section">
    <div class="unified-header-bar">
      <!-- Left: Status Filter Chips -->
      <div class="filter-chips-group">
        <button
          class="chip-button status-chip"
          [class.active]="isStatusActive(1)"
          (click)="toggleStatus(1)">
          <mat-icon class="chip-icon">block</mat-icon>
          Disabled
        </button>
        <button
          class="chip-button status-chip"
          [class.active]="isStatusActive(2)"
          (click)="toggleStatus(2)">
          <mat-icon class="chip-icon">flight_takeoff</mat-icon>
          Limited Access
        </button>
        <button
          class="chip-button status-chip"
          [class.active]="isStatusActive(3)"
          (click)="toggleStatus(3)">
          <mat-icon class="chip-icon">check_circle</mat-icon>
          Full Access
        </button>
      </div>

      <!-- Right: Category, Search, Clear, and New Feature -->
      <div class="header-actions-group">
        <mat-form-field appearance="outline" class="category-field">
          <mat-label>Category</mat-label>
          <mat-select [(ngModel)]="selectedCategory" (selectionChange)="applyFilter()">
            <mat-option value="">All</mat-option>
            <mat-option value="Book">Book</mat-option>
            <mat-option value="General">General</mat-option>
            <mat-option value="Key">Key</mat-option>
            <mat-option value="KeySystem">Key System</mat-option>
            <mat-option value="Structure">Structure</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="search-field">
          <mat-label>Search</mat-label>
          <input
            matInput
            [(ngModel)]="searchText"
            (ngModelChange)="searchSubject.next($event)"
            (keyup.enter)="applyFilter()"
            placeholder="Search by name or description"
          />
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <button mat-button (click)="clearFilters()" class="clear-btn">
          <mat-icon>clear</mat-icon>
          Clear All
        </button>

        <button mat-raised-button color="primary" (click)="createFeature()" class="new-feature-btn">
          <mat-icon>add</mat-icon>
          New Feature
        </button>
      </div>
    </div>
  </div>

  <div class="table-section">
    <div class="table-scroll-wrapper">
      <div class="loading-overlay" *ngIf="loading">
        <mat-spinner diameter="40"></mat-spinner>
        <p style="margin-top: 16px; font-size: 14px; color: #757575;">Loading features...</p>
      </div>

      <!-- No Results State -->
      <div class="no-results" *ngIf="!loading && dataSource.data.length === 0">
        <mat-icon class="no-results-icon">{{ selectedStatus !== null || selectedCategory || searchText ? 'search_off' : 'flag' }}</mat-icon>
        <h3>{{ selectedStatus !== null || selectedCategory || searchText ? 'No records found' : 'No features found' }}</h3>
        <p>{{ selectedStatus !== null || selectedCategory || searchText ? 'No features match the selected filters.' : 'Create your first feature flag to get started with controlled feature rollouts.' }}</p>
        <button mat-raised-button color="primary" *ngIf="!selectedStatus && !selectedCategory && !searchText" (click)="createFeature()">
          <mat-icon>add</mat-icon>
          Create Feature
        </button>
      </div>

      <table mat-table [dataSource]="dataSource" matSort class="features-table" [style.display]="!loading && dataSource.data.length > 0 ? 'table' : 'none'">
        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
          <td mat-cell *matCellDef="let feature">
            <div class="status-cell">
              <mat-icon
                class="status-icon"
                [class.pilot-icon]="feature.statusId === 2"
                [class.disabled-icon]="feature.statusId === 1"
                [class.full-access-icon]="feature.statusId === 3"
              >
                {{ getStatusIcon(feature.statusId) }}
              </mat-icon>
              <span class="status-text">
                {{ getEffectiveStatusText(feature) }}
              </span>
            </div>
          </td>
        </ng-container>

        <!-- Name Column -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Feature</th>
          <td mat-cell *matCellDef="let feature">
            <div class="name-cell">
              <div class="name-row">
                <strong>{{ feature.name }}</strong>
                <span class="category-badge-inline">{{ feature.category }}</span>
              </div>
              <p class="description-text">{{ feature.description }}</p>
            </div>
          </td>
        </ng-container>

        <!-- Rollout Percentage Column - COMMENTED OUT (not using gradual rollout currently) -->
        <!-- Uncomment this section when gradual rollout is needed -->
        <!--
        <ng-container matColumnDef="rolloutPercentage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Rollout</th>
          <td mat-cell *matCellDef="let feature">
            <div class="rollout-cell">
              <span class="rollout-text">{{ feature.rolloutPercentage }}%</span>
              <mat-progress-bar
                mode="determinate"
                [value]="feature.rolloutPercentage"
                [color]="feature.rolloutPercentage === 100 ? 'primary' : 'accent'"
              ></mat-progress-bar>
            </div>
          </td>
        </ng-container>
        -->

        <!-- Access Summary Column -->
        <ng-container matColumnDef="accessSummary">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Access Summary</th>
          <td mat-cell *matCellDef="let feature">
            <div
              class="access-summary-container"
              [matTooltip]="parseAccessSummary(feature.accessSummary).additionalCount > 0 ? getAccessTooltip(feature.accessSummary) : ''"
              matTooltipClass="access-tooltip"
              [class.not-configured]="!feature.accessSummary && feature.statusId === 2">
              <ng-container *ngIf="feature.accessSummary; else noAccess">
                <ng-container *ngIf="parseAccessSummary(feature.accessSummary).summary; else detailedAccess">
                  <span class="access-value">{{ parseAccessSummary(feature.accessSummary).summary }}</span>
                </ng-container>
                <ng-template #detailedAccess>
                  <div class="access-summary-line" *ngIf="parseAccessSummary(feature.accessSummary).roles">
                    <span class="access-label">Roles:</span> <span class="access-value">{{ parseAccessSummary(feature.accessSummary).roleValues }}</span>
                  </div>
                  <div class="access-summary-line users-line" *ngIf="parseAccessSummary(feature.accessSummary).users">
                    <span class="access-label">Users:</span> <span class="access-value">{{ parseAccessSummary(feature.accessSummary).users }}</span><span class="additional-users" *ngIf="parseAccessSummary(feature.accessSummary).additionalCount > 0"> +{{ parseAccessSummary(feature.accessSummary).additionalCount }} more</span>
                  </div>
                </ng-template>
              </ng-container>
              <ng-template #noAccess>
                <span class="no-access">{{ feature.statusId === 2 ? 'Not configured' : '--' }}</span>
              </ng-template>
            </div>
          </td>
        </ng-container>

        <!-- Modified Date Column -->
        <ng-container matColumnDef="modifiedDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Last Modified</th>
          <td mat-cell *matCellDef="let feature">
            <div class="modified-cell">
              <span>{{ (feature.modifiedOn || feature.createdOn) | date: 'MMM d, yyyy, h:mm a' }}</span>
              <small>by {{ feature.modifiedByName || feature.createdByName }}</small>
            </div>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let feature">
            <button
              mat-icon-button
              [matMenuTriggerFor]="menu"
              (click)="$event.stopPropagation()"
            >
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="editFeature(feature)">
                <mat-icon color="primary">edit</mat-icon>
                <span>Edit</span>
              </button>
              <button
                mat-menu-item
                (click)="setFullAccess(feature, $event)"
                *ngIf="feature.statusId !== 3"
              >
                <mat-icon class="full-access-menu-icon">check_circle</mat-icon>
                <span>Set to Full Access</span>
              </button>
              <button
                mat-menu-item
                (click)="setDisabled(feature, $event)"
                *ngIf="feature.statusId !== 1"
                class="disable-action"
              >
                <mat-icon class="disable-icon">cancel</mat-icon>
                <span>Disable</span>
              </button>
              <button
                mat-menu-item
                (click)="setLimitedAccess(feature, $event)"
                *ngIf="feature.statusId !== 2"
                class="pilot-action"
              >
                <mat-icon class="pilot-menu-icon">flight_takeoff</mat-icon>
                <span>Set to Limited Access</span>
              </button>
            </mat-menu>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          class="feature-row"
          (click)="editFeature(row)"
        ></tr>
      </table>
    </div>

      <mat-paginator
        [style.display]="!loading && dataSource.data.length > 0 ? 'block' : 'none'"
        [pageSizeOptions]="[5, 10, 25, 50, 100]"
        [pageSize]="pageSize"
        [length]="totalCount"
        (page)="onPageChange($event)"
        showFirstLastButtons
        aria-label="Feature list pagination"
      ></mat-paginator>
  </div>
</div>
